import { useContext, useEffect, useRef, useCallback, useMemo } from 'react'
import { View, Text, Pressable, Image, Animated, Platform, useWindowDimensions } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { useRouter, type Href } from 'expo-router'
import { Check, Diamond, Zap, Shield, Users } from 'lucide-react-native'
import { LANDING_PHONE_MAX_WIDTH } from '../../../src/landing/constants/landingMarketingBreakpoints.constants'
import { HERO_COPY_BY_LANGUAGE } from './LandingHero.constants'
import { LanguageContext } from '../../../src/i18n'
import { useTheme } from '../../../src/theme/useTheme'
import { useThemeContext } from '../../../src/theme/ThemeContext'
import {
  ASSET_PATHS,
  getCdnUrlExactKey,
  APP_STORE_BADGE_SOURCE,
  GOOGLE_PLAY_BADGE_SOURCE,
} from '../../../constants/assets'
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '../../../constants/storeLinks'
import { openExternalUrl } from '../../../src/shared/helpers/openExternalUrl.helper'
import {
  getLandingHeroStyles,
  getLandingHeroSectionBgStyle,
  landingHeroGradientTextStyle,
} from './LandingHero.styles'
import { getLandingHeroVisualStyles } from './LandingHeroVisual.styles'

const HERO_STACKED_WIDTH = 980
const HERO_PHONE_WIDTH = LANDING_PHONE_MAX_WIDTH

export function LandingHero() {
  const theme = useTheme()
  const { isDark } = useThemeContext()
  const styles = useMemo(() => getLandingHeroStyles(theme), [theme])
  const visualStyles = useMemo(() => getLandingHeroVisualStyles(theme), [theme])
  const sectionBgStyle = useMemo(() => getLandingHeroSectionBgStyle(theme, isDark), [theme, isDark])
  const router = useRouter()
  const { width } = useWindowDimensions()
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('LandingHero must be used within LanguageProvider')
  }
  const { language, t } = languageContext
  const isStacked = width > 0 && width < HERO_STACKED_WIDTH
  const isPhone = width > 0 && width < HERO_PHONE_WIDTH

  const heroCopy = HERO_COPY_BY_LANGUAGE[language] ?? HERO_COPY_BY_LANGUAGE.en

  const mascotAnim = useRef(new Animated.Value(0)).current
  const card1Anim = useRef(new Animated.Value(0)).current
  const card2Anim = useRef(new Animated.Value(0)).current
  const card3Anim = useRef(new Animated.Value(0)).current
  const shineAnim = useRef(new Animated.Value(-90)).current

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(mascotAnim, { toValue: -12, duration: 2800, useNativeDriver: true }),
        Animated.timing(mascotAnim, { toValue: 0, duration: 2800, useNativeDriver: true }),
      ]),
    ).start()
    Animated.loop(
      Animated.sequence([
        Animated.timing(card1Anim, { toValue: -8, duration: 3200, useNativeDriver: true }),
        Animated.timing(card1Anim, { toValue: 0, duration: 3200, useNativeDriver: true }),
      ]),
    ).start()
    Animated.loop(
      Animated.sequence([
        Animated.delay(600),
        Animated.timing(card2Anim, { toValue: -10, duration: 2600, useNativeDriver: true }),
        Animated.timing(card2Anim, { toValue: 0, duration: 2600, useNativeDriver: true }),
      ]),
    ).start()
    Animated.loop(
      Animated.sequence([
        Animated.delay(1200),
        Animated.timing(card3Anim, { toValue: -8, duration: 3000, useNativeDriver: true }),
        Animated.timing(card3Anim, { toValue: 0, duration: 3000, useNativeDriver: true }),
      ]),
    ).start()
    Animated.loop(
      Animated.sequence([
        Animated.delay(10000),
        Animated.timing(shineAnim, { toValue: 180, duration: 950, useNativeDriver: true }),
        Animated.timing(shineAnim, { toValue: -90, duration: 0, useNativeDriver: true }),
      ]),
    ).start()
  }, [mascotAnim, card1Anim, card2Anim, card3Anim, shineAnim])

  // Guest-first: the hero's primary CTA leads into the Welcome front door
  // (Get Started → tutorial → guest, or "I already have an account"). A signed-in
  // user hitting /welcome is redirected straight into the app. See specs/guest-first-onboarding (AC-4).
  const goGetStarted = useCallback(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open('/welcome', '_blank', 'noopener,noreferrer')
      return
    }
    router.push('/welcome' as Href)
  }, [router])

  const openAppStore = useCallback(() => openExternalUrl(APP_STORE_URL), [])
  const openGooglePlay = useCallback(() => openExternalUrl(GOOGLE_PLAY_URL), [])

  return (
    <View style={[styles.section, sectionBgStyle, isPhone && styles.sectionPhone]}>
      <View style={[styles.bgBlob, styles.bgBlobPrimary]} />
      <View style={[styles.bgBlob, styles.bgBlobGold]} />
      <View style={styles.container}>
        <View style={[styles.grid, isStacked && styles.gridStacked, isPhone && styles.gridPhone]}>
          {/* Copy */}
          <View style={[styles.copy, isStacked && styles.copyStacked]}>
            <Text style={[styles.h1, isStacked && styles.h1Stacked, isPhone && styles.h1Phone]}>
              {`${heroCopy.line1}\n`}
              <Text style={[styles.h1Accent, landingHeroGradientTextStyle]}>{heroCopy.accent}</Text>
              {' '}
              <Text style={styles.h1Underline}>{heroCopy.underline}</Text>
            </Text>

            <Text style={[styles.lede, isStacked && styles.ledeStacked, isPhone && styles.ledePhone]}>
              {heroCopy.lede}
            </Text>

            <View style={[styles.ctaRow, isPhone && styles.ctaRowPhone]}>
              <Pressable
                accessibilityRole="link"
                onPress={goGetStarted}
                style={[styles.ctaPressable, isPhone && styles.ctaPressablePhone]}
              >
                <LinearGradient
                  colors={['#9F7AEA', '#6B46C1']}
                  start={{ x: 0.5, y: 0 }}
                  end={{ x: 0.5, y: 1 }}
                  style={[styles.btnPrimary, isPhone && styles.btnPrimaryPhone]}
                >
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      styles.btnPrimaryShine,
                      { transform: [{ translateX: shineAnim }, { rotate: '18deg' }] },
                    ]}
                  />
                  <Text style={styles.btnPrimaryText}>{heroCopy.primaryCta}</Text>
                </LinearGradient>
              </Pressable>
            </View>

            <View
              style={[
                styles.storeBadges,
                (isStacked || isPhone) && styles.storeBadgesCentered,
              ]}
            >
              <Pressable
                accessibilityRole="link"
                accessibilityLabel="Download on the App Store"
                onPress={openAppStore}
                style={({ hovered }) => [
                  styles.storeBadgePressable,
                  hovered && styles.storeBadgeHovered,
                ]}
              >
                <Image
                  source={APP_STORE_BADGE_SOURCE}
                  style={[styles.appStoreBadge, isPhone && styles.appStoreBadgePhone]}
                  resizeMode="contain"
                  accessibilityIgnoresInvertColors
                />
              </Pressable>
              <Pressable
                accessibilityRole="link"
                accessibilityLabel="Get it on Google Play"
                onPress={openGooglePlay}
                style={({ hovered }) => [
                  styles.storeBadgePressable,
                  hovered && styles.storeBadgeHovered,
                ]}
              >
                <Image
                  source={GOOGLE_PLAY_BADGE_SOURCE}
                  style={[styles.googlePlayBadge, isPhone && styles.googlePlayBadgePhone]}
                  resizeMode="contain"
                  accessibilityIgnoresInvertColors
                />
              </Pressable>
            </View>

            <View style={[styles.trustRow, isStacked && styles.trustRowWhenStacked]}>
              <View style={[styles.trustPill, isPhone && styles.trustPillPhone]}>
                <Shield size={13} color={theme.gray[900]} />
                <Text style={[styles.trustPillText, isPhone && styles.trustPillTextPhone]}>{heroCopy.trustA}</Text>
              </View>
              <View style={[styles.trustPill, isPhone && styles.trustPillPhone]}>
                <Zap size={13} color={theme.gray[900]} />
                <Text style={[styles.trustPillText, isPhone && styles.trustPillTextPhone]}>{heroCopy.trustB}</Text>
              </View>
              <View style={[styles.trustPill, isPhone && styles.trustPillPhone]}>
                <Users size={13} color={theme.gray[900]} />
                <Text style={[styles.trustPillText, isPhone && styles.trustPillTextPhone]}>{heroCopy.trustC}</Text>
              </View>
            </View>
          </View>

          {/* Visual */}
          <View style={[visualStyles.visual, isStacked && visualStyles.visualStacked, isPhone && visualStyles.visualPhone]}>
            <View style={[visualStyles.mascotGlow, isPhone && visualStyles.mascotGlowPhone]} />

            <Animated.View
              style={[
                visualStyles.mascotWrap,
                isPhone && visualStyles.mascotWrapPhone,
                { transform: [{ translateY: mascotAnim }] },
              ]}
            >
              <Image
                source={{ uri: getCdnUrlExactKey(ASSET_PATHS.LANDING_HERO_MASCOT) }}
                style={[
                  visualStyles.mascotImage,
                  isStacked && visualStyles.mascotImageStacked,
                  isPhone && visualStyles.mascotImagePhone,
                ]}
                resizeMode="cover"
                accessibilityLabel={t.a11y.heroMascot}
                accessibilityIgnoresInvertColors
              />
            </Animated.View>

            {/* Float card 1 — +5 pts */}
            <Animated.View
              style={[
                visualStyles.floatCard,
                isPhone && visualStyles.floatCardPhone,
                visualStyles.floatCard1,
                isPhone && visualStyles.floatCard1Phone,
                { transform: [{ translateY: card1Anim }] },
              ]}
            >
              <View style={[visualStyles.floatCardIcon, visualStyles.fcIconWin]}>
                <Check size={16} color={theme.secondary} />
              </View>
              <View>
                <Text style={visualStyles.floatCardStrong}>+5 pts</Text>
                <Text style={visualStyles.floatCardSpan}>{heroCopy.cardResultLabel}</Text>
              </View>
            </Animated.View>

            {/* Float card 2 — Boost */}
            <Animated.View
              style={[
                visualStyles.floatCard,
                isPhone && visualStyles.floatCardPhone,
                visualStyles.floatCard2,
                isPhone && visualStyles.floatCard2Phone,
                { transform: [{ translateY: card2Anim }] },
              ]}
            >
              <View style={[visualStyles.floatCardIcon, visualStyles.fcIconCoin]}>
                <Diamond size={16} color={theme.accentDark} />
              </View>
              <View>
                <Text style={visualStyles.floatCardStrong}>{heroCopy.cardBoostTitle}</Text>
                <Text style={visualStyles.floatCardSpan}>{heroCopy.cardBoostSubtitle}</Text>
              </View>
            </Animated.View>

            {/* Float card 3 — rank up */}
            <Animated.View
              style={[
                visualStyles.floatCard,
                isPhone && visualStyles.floatCardPhone,
                visualStyles.floatCard3,
                isPhone && visualStyles.floatCard3Phone,
                { transform: [{ translateY: card3Anim }] },
              ]}
            >
              <View style={[visualStyles.floatCardIcon, visualStyles.fcIconBolt]}>
                <Zap size={16} color={theme.primary} />
              </View>
              <View>
                <Text style={visualStyles.floatCardStrong}>{heroCopy.cardRankTitle}</Text>
                <Text style={visualStyles.floatCardSpan}>{heroCopy.cardRankSubtitle}</Text>
              </View>
            </Animated.View>
          </View>
        </View>
      </View>
    </View>
  )
}
